import { PageMeta } from "@/components/common/PageMeta";
import { PageHero } from "@/components/common/PageHero";
import { ABOUT_IS, ABOUT_IS_NOT, PRINCIPLES, GOVERNANCE, HERO_ALT, ABOUT_OEM } from "@/lib/content";
import { GovernanceHero } from "@/components/governance/GovernanceHero";
import { GovernancePillarCard } from "@/components/governance/GovernancePillarCard";
import { LegalIdentityPanel } from "@/components/governance/LegalIdentityPanel";
import { TrustStatement } from "@/components/governance/TrustStatement";
import { Check, X } from "lucide-react";

export default function About() {
  return (
    <>
      <PageMeta title="About" description="How Digital Brain Research Foundation structures scientific leadership, multidisciplinary collaboration, programme governance, confidentiality and intellectual-property stewardship." />
      <PageHero
        eyebrow="About DBRF"
        title="A foundation for difficult, interdisciplinary work."
        intro="To make rare scientific and engineering capability easier to assemble, govern and direct toward consequential real-world problems."
        image="/assets/07-about-hero.webp"
        alt={HERO_ALT.about}
        objectPosition="center 35%"
      />

      <section className="container-dbrf py-16 lg:py-24">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <p className="eyebrow mb-4">What DBRF is</p>
            <ul className="space-y-3">
              {ABOUT_IS.map((x) => <li key={x} className="flex gap-3"><Check size={18} className="mt-0.5 flex-shrink-0 text-[var(--success)]" /><span className="text-navy">{x}</span></li>)}
            </ul>
          </div>
          <div>
            <p className="eyebrow mb-4">What DBRF is not</p>
            <ul className="space-y-3">
              {ABOUT_IS_NOT.map((x) => <li key={x} className="flex gap-3"><X size={18} className="mt-0.5 flex-shrink-0 text-[var(--error)]" /><span className="text-[var(--mineral)]">{x}</span></li>)}
            </ul>
          </div>
        </div>
      </section>

      <section className="border-y border-[var(--line)] bg-[var(--paper)] py-16 lg:py-24">
        <div className="container-dbrf">
          <p className="eyebrow mb-4">Principles</p>
          <h2 className="font-display font-bold text-navy" style={{ fontSize: "clamp(1.6rem,2.6vw,2.4rem)" }}>How DBRF works, held to a standard.</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {PRINCIPLES.map((p, i) => (
              <div key={p.t} className="card-lift rounded-2xl border border-[var(--line)] bg-[var(--ivory)] p-6">
                <span className="font-mono text-sm text-[var(--cobalt)]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-2 font-display text-lg font-bold text-navy">{p.t}</h3>
                <p className="mt-2 text-sm text-[var(--mineral)]">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-[var(--line)] bg-[var(--navy)] py-16 text-[var(--ivory)] lg:py-24" data-nav-dark data-testid="about-oem">
        <div className="container-dbrf reading">
          <p className="eyebrow mb-4 !text-[var(--cyan)]">OEM development</p>
          <h2 className="font-display font-bold" style={{ fontSize: "clamp(1.6rem,2.6vw,2.4rem)" }}>{ABOUT_OEM.heading}</h2>
          <p className="mt-5 text-lg text-[var(--ivory)]/80" style={{ lineHeight: 1.7 }}>{ABOUT_OEM.copy}</p>
        </div>
      </section>

      <section className="container-dbrf py-16 lg:py-24">
        <GovernanceHero eyebrow={GOVERNANCE.eyebrow} heading={GOVERNANCE.heading} intro={GOVERNANCE.intro} />
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:mt-16">
          {GOVERNANCE.pillars.map((p) => (
            <GovernancePillarCard key={p.key} id={p.key} title={p.title} body={p.body} tags={p.tags} />
          ))}
        </div>
      </section>

      <LegalIdentityPanel
        eyebrow={GOVERNANCE.legal.eyebrow}
        name={GOVERNANCE.legal.name}
        intro={GOVERNANCE.legal.intro}
        fields={GOVERNANCE.legal.fields}
      />

      <TrustStatement
        heading={GOVERNANCE.trust.heading}
        copy={GOVERNANCE.trust.copy}
        ctaLabel={GOVERNANCE.trust.ctaLabel}
        ctaTo={GOVERNANCE.trust.ctaTo}
      />
    </>
  );
}
